import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { ApiResponse } from '../interfaces/common.interfaces';
import {
  PolicyRequest,
  PolicyData,
  PolicyLookupResponse,
  ProductionRequest,
  ProductionData,
  ProducerLookupResponse,
  PolicyCancellationRequest,
  PolicyCancellationResponse,
  PolicyRenewalRequest,
  PolicyRenewalResponse,
} from '../interfaces/micartera.interfaces';
import { map } from 'rxjs/operators';

/**
 * Servicio para gestionar las operaciones relacionadas con la cartera de pólizas
 */
@Injectable({
  providedIn: 'root',
})
export class MicarteraService {
  private apiUrl = environment.int365_dom + '/api/v1';
  private cachedProductionLookups: ApiResponse<ProducerLookupResponse> | null =
    null;
  private cachedPolicyLookups: ApiResponse<PolicyLookupResponse> | null = null;

  constructor(private http: HttpClient, private authService: AuthService) {}

  /**
   * Inicializa el servicio cargando los lookups necesarios si el usuario está autenticado
   */
  public initializeService(): void {
    if (this.authService.isAuthenticated()) {
      this.initializeLookups();
    }
  }

  /**
   * Inicializa los lookups de producción y pólizas si no están en caché
   */
  private initializeLookups(): void {
    if (!this.cachedProductionLookups) {
      this.loadProductionLookups().subscribe({
        error: (error) =>
          console.error('Error cargando production lookups:', error),
      });
    }
    if (!this.cachedPolicyLookups) {
      this.loadPolicyLookups().subscribe({
        error: (error) =>
          console.error('Error cargando policy lookups:', error),
      });
    }
  }

  /**
   * Carga los lookups de producción desde el servidor
   * @returns Observable con la respuesta de los lookups de producción
   */
  private loadProductionLookups(): Observable<
    ApiResponse<ProducerLookupResponse>
  > {
    return this.http
      .get<ApiResponse<ProducerLookupResponse>>(
        `${this.apiUrl}/Lookup/Filters`,
        this.getHttpOptions()
      )
      .pipe(
        map((response) => {
          this.cachedProductionLookups = response;
          return response;
        })
      );
  }

  /**
   * Carga los lookups de pólizas desde el servidor
   * @returns Observable con la respuesta de los lookups de pólizas
   */
  private loadPolicyLookups(): Observable<ApiResponse<PolicyLookupResponse>> {
    return this.http
      .get<ApiResponse<PolicyLookupResponse>>(
        `${this.apiUrl}/Lookup/lob-slob`,
        this.getHttpOptions()
      )
      .pipe(
        map((response) => {
          this.cachedPolicyLookups = response;
          return response;
        })
      );
  }

  /**
   * Obtiene los lookups de producción, usando caché si está disponible
   * @returns Observable con los lookups de producción
   */
  getProductionLookups(): Observable<ApiResponse<ProducerLookupResponse>> {
    if (this.cachedProductionLookups) {
      return of(this.cachedProductionLookups);
    }
    return this.loadProductionLookups();
  }

  /**
   * Obtiene los lookups de pólizas, usando caché si está disponible
   * @returns Observable con los lookups de pólizas
   */
  getPolicyLookups(): Observable<ApiResponse<PolicyLookupResponse>> {
    if (this.cachedPolicyLookups) {
      return of(this.cachedPolicyLookups);
    }
    return this.loadPolicyLookups();
  }

  /**
   * Obtiene las pólizas según los criterios de búsqueda
   * @param request Criterios de búsqueda de pólizas
   * @returns Observable con la respuesta HTTP que contiene las pólizas
   */
  getPolicies(
    request: PolicyRequest
  ): Observable<HttpResponse<ApiResponse<PolicyData>>> {
    return this.http.get<ApiResponse<PolicyData>>(
      `${this.apiUrl}/Policy/policies`,
      {
        ...this.getHttpOptions(request),
        observe: 'response',
        responseType: 'json',
      }
    );
  }

  /**
   * Obtiene la producción según los criterios de búsqueda
   * @param request Criterios de búsqueda de producción
   * @returns Observable con la respuesta de producción
   */
  getProduction(
    request: ProductionRequest
  ): Observable<HttpResponse<ApiResponse<ProductionData>>> {
    return this.http.get<ApiResponse<ProductionData>>(
      `${this.apiUrl}/Production/production`,
      {
        ...this.getHttpOptions(request),
        observe: 'response',
        responseType: 'json',
      }
    );
  }

  /**
   * Obtiene las renovaciones de pólizas según los criterios de búsqueda
   * @param request Criterios de búsqueda de renovaciones
   * @returns Observable con la respuesta de renovaciones
   */
  getPolicyRenewals(
    request: PolicyRenewalRequest
  ): Observable<HttpResponse<ApiResponse<PolicyRenewalResponse[]>>> {
    return this.http.get<ApiResponse<PolicyRenewalResponse[]>>(
      `${this.apiUrl}/PolicyRenewals/renewals`,
      {
        ...this.getHttpOptions(request),
        observe: 'response',
        responseType: 'json',
      }
    );
  }

  /**
   * Obtiene las cancelaciones de pólizas según los criterios de búsqueda
   * @param request Criterios de búsqueda de cancelaciones
   * @returns Observable con la respuesta de cancelaciones
   */
  getPolicyCancellations(
    request: PolicyCancellationRequest
  ): Observable<HttpResponse<ApiResponse<PolicyCancellationResponse[]>>> {
    return this.http.get<ApiResponse<PolicyCancellationResponse[]>>(
      `${this.apiUrl}/Cancellation/cancellations`,
      {
        ...this.getHttpOptions(request),
        observe: 'response',
        responseType: 'json',
      }
    );
  }

  /**
   * Configura las opciones HTTP incluyendo headers y parámetros
   * @param params Parámetros opcionales para la petición
   * @returns Objeto con las opciones HTTP configuradas
   */
  private getHttpOptions(params?: any) {
    if (!this.authService.isAuthenticated()) {
      throw new Error('Usuario no autenticado');
    }

    const token = this.authService.getToken();
    if (!token) {
      throw new Error('No token found');
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return {
      headers,
      params: params
        ? new HttpParams({ fromObject: this.formatParams(params) })
        : undefined,
    };
  }

  /**
   * Formatea los parámetros para las peticiones HTTP
   * @param params Objeto con los parámetros a formatear
   * @returns Objeto con los parámetros formateados
   */
  private formatParams(params: any): { [key: string]: string } {
    const formattedParams: { [key: string]: string } = {};

    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined) return;

      if (value instanceof Date) {
        formattedParams[key] = value.toISOString().split('T')[0];
      } else {
        formattedParams[key] = String(value);
      }
    });

    return formattedParams;
  }

  /**
   * Limpia el caché de lookups
   */
  clearCache(): void {
    this.cachedProductionLookups = null;
    this.cachedPolicyLookups = null;
  }
}
